import styled, { css } from 'styled-components';
import { Colors } from 'styles/enums';
import { mediaQueries } from 'styles/helpers';
import { CARD_TYPES } from './constants';

const { PERSON_WITH_IMAGE, BLOG_POST_CARD, BASE_CARD, SIMPLE_PRESENTATION_CARD } = CARD_TYPES;

type WithThemeProp = {
  theme: string;
};

const generateDimensions = (theme: string) => {
  return css`
    ${mediaQueries(
      'md',
      css`
        min-width: calc((100% - 20px) / 2);
      `
    )}
    ${mediaQueries(
      'lg',
      css`
        min-width: ${theme === SIMPLE_PRESENTATION_CARD ? 'calc((100% - 80px) / 4)' : 'calc((100% - 80px) / 3)'};
      `
    )}
  `;
};

/**
 * Styled Components has a "theme" functionality, so you can
 * actually define a global theme and use it in your components.
 *
 * So if the props for the styles below were not defined specifically, it causes linting issues
 * ./components/aurora/card/card.styles.ts:11:12
 *  Type error: Type 'string' is not comparable to type 'DefaultTheme'.
 *
 * * Thats why I defined the WithThemeProp type and used it in the styled components below.
 */
export const ContentWrapper = styled.div<WithThemeProp>`
  display: flex;
  flex-direction: column;
  border-top: ${({ theme }) => {
    switch (theme) {
      case PERSON_WITH_IMAGE:
        return 'none';
      case BASE_CARD:
        return 'none';
      case SIMPLE_PRESENTATION_CARD:
        return 'none';
      default:
        return '3px solid #ffda00';
    }
  }};
  align-items: ${({ theme }) => (theme === BLOG_POST_CARD ? 'flex-start' : 'center')};
  margin-top: 0px;
  text-align: ${({ theme }) => (theme === SIMPLE_PRESENTATION_CARD ? 'center' : 'auto')};
  padding: ${({ theme }) => (theme === SIMPLE_PRESENTATION_CARD ? '0' : '20px 0px')};
  gap: 20px;
`;

export const ImageWrapper = styled.div<WithThemeProp>`
  display: ${({ theme }) => (theme === BLOG_POST_CARD || theme === PERSON_WITH_IMAGE ? 'flex' : 'none')};
  border: ${({ theme }) => (theme === PERSON_WITH_IMAGE ? ' 4px solid #ffda00' : 'none')};
  border-radius: ${({ theme }) => (theme === PERSON_WITH_IMAGE ? '50%' : '0')};
  overflow: ${({ theme }) => (theme === PERSON_WITH_IMAGE ? 'hidden' : 'auto')};
  width: ${({ theme }) => (theme !== PERSON_WITH_IMAGE ? '100%' : '10rem')};
  height: ${({ theme }) => (theme !== PERSON_WITH_IMAGE ? '12rem' : '10rem')};
  overflow: hidden;

  @media (max-width: 576px) {
    display: ${({ theme }) => (theme === PERSON_WITH_IMAGE ? 'none' : 'flex')};
  }
`;

export const CardWrapper = styled.div<{ $backgroundcolor?: string; theme: string }>`
  display: flex;
  flex-direction: column;
  position: relative;
  padding: 20px 0px;
  border: ${({ $backgroundcolor }) => ($backgroundcolor ? '5px solid' : 'none')};
  border-radius: ${({ $backgroundcolor }) => ($backgroundcolor ? '10px' : 'none')};
  border-color: ${({ $backgroundcolor }) => $backgroundcolor};
  max-width: ${({ theme }) =>
    theme === SIMPLE_PRESENTATION_CARD ? 'calc((100% - 80px) / 4)' : 'calc((100% - 80px) / 3)'};
  min-width: calc(100% - 20px);
  ${({ theme }) => generateDimensions(theme)}

  ${ImageWrapper} {
    align-self: center;
  }
`;

export const DescriptionWrapper = styled.div<WithThemeProp>`
  max-height: ${({ theme }) => (theme === BASE_CARD ? '16rem' : '6rem')};
  white-space: normal;
  text-align: ${({ theme }) => (theme === SIMPLE_PRESENTATION_CARD || theme === BASE_CARD ? 'center' : 'inherit')};
  overflow: ${({ theme }) => (theme === SIMPLE_PRESENTATION_CARD || theme === BASE_CARD ? 'visible' : 'hidden')};
  width: ${({ theme }) => (theme === BASE_CARD ? '80%' : '100%')};
  [data-cy='Text'] {
    color: ${({ theme }) => theme === BASE_CARD && `${Colors.Gray.Gray19} !important`};
  }
`;

export const SimplePresentationCardBorder = styled.div`
  border-bottom: 3px solid #ffda00;
  position: absolute;
  bottom: 0px;
  left: 35%;
  right: 35%;
`;
